import { useState } from "react";
import { Plus, Pencil, Trash2, Clock } from "lucide-react";
import { useRoutines, useDeleteRoutine, type RoutineSummary } from "../../lib/api/useRoutines";
import { useToast } from "../../lib/toast/ToastProvider";
import { ApiError } from "../../lib/api/client";
import { RoutineDialog } from "./RoutineDialog";
import { ConfirmDialog } from "../ui/ConfirmDialog";
import { SectionCard } from "../ui/SectionCard";
import { Button } from "../ui/Button";
import { Spinner } from "../ui/Spinner";
import { EmptyState } from "../ui/EmptyState";
import { SECTION_ACCENT } from "../../lib/accents";
import { INLINE_STATUS, ROW } from "../../lib/surfaces";
import { cn } from "../../lib/cn";
import { FOCUS_RING } from "../../lib/focusRing";

const DAYPART_LABELS: Record<RoutineSummary["daypart"], string> = {
  morning: "Morning",
  midday: "Midday",
  afternoon: "Afternoon",
  evening: "Evening",
  bedtime: "Bedtime",
};

const CATEGORY_LABELS: Record<RoutineSummary["category"], string> = {
  nap: "Nap",
  meal: "Meal",
  activity: "Activity",
  medication: "Medication",
  bedtime: "Bedtime",
  other: "Other",
};

export function RoutineList({ childId }: { childId: string }) {
  const { data: routines, isLoading, error } = useRoutines(childId);
  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState<RoutineSummary | undefined>();
  const [deleting, setDeleting] = useState<RoutineSummary | undefined>();
  const deleteMutation = useDeleteRoutine(childId);
  const { publish } = useToast();

  const handleDelete = async () => {
    if (!deleting) return;
    try {
      await deleteMutation.mutateAsync(deleting.id);
      publish({ variant: "success", title: "Routine deleted successfully" });
      setDeleting(undefined);
    } catch (err) {
      publish({
        variant: "error",
        title: "Failed to delete routine",
        description: err instanceof ApiError ? err.message : undefined,
      });
      setDeleting(undefined);
    }
  };

  return (
    <SectionCard
      icon={Clock}
      title="Daily Routines"
      accent={SECTION_ACCENT.routines}
      actions={
        <Button size="sm" onClick={() => setCreateOpen(true)}>
          <Plus className="h-4 w-4" aria-hidden />
          Add Routine
        </Button>
      }
    >
      {isLoading && (
        <div role="status" className={INLINE_STATUS}>
          <Spinner size="sm" />
          Loading…
        </div>
      )}
      {error && <p className="text-sm text-red-600">{error.message}</p>}

      {!isLoading && !error && (routines?.length ?? 0) === 0 && (
        <EmptyState
          size="sm"
          accent="indigo"
          icon={Clock}
          title="No routines yet"
          description="Add the daily rhythm — naps, meals, activities, bedtime — so a caregiver knows what to expect and when."
        />
      )}

      {!isLoading && !error && routines && routines.length > 0 && (
        <ul className="grid grid-cols-1 gap-3 lg:grid-cols-2">
          {routines.map((routine) => (
            <li
              key={routine.id}
              className={ROW}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <h3 className="text-base font-semibold text-neutral-900">{routine.label}</h3>
                    <span className="rounded-full bg-indigo-100 px-2 py-0.5 text-xs font-medium text-indigo-700">
                      {DAYPART_LABELS[routine.daypart]}
                      {routine.timeText ? ` · ${routine.timeText}` : ""}
                    </span>
                    <span className="rounded-full bg-neutral-200 px-2 py-0.5 text-xs font-medium text-neutral-700">
                      {CATEGORY_LABELS[routine.category]}
                    </span>
                  </div>
                  {routine.details && (
                    <p className="mt-1 text-sm text-neutral-700">{routine.details}</p>
                  )}
                  {routine.notes && (
                    <p className="mt-2 text-xs italic text-neutral-600">{routine.notes}</p>
                  )}
                </div>
                <div className="flex flex-shrink-0 gap-1">
                  <button
                    type="button"
                    onClick={() => setEditing(routine)}
                    aria-label={`Edit ${routine.label}`}
                    className={cn(
                      "rounded-full p-1.5 text-neutral-600 transition hover:bg-white hover:text-neutral-900",
                      FOCUS_RING,
                    )}
                  >
                    <Pencil className="h-4 w-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => setDeleting(routine)}
                    aria-label={`Delete ${routine.label}`}
                    className={cn(
                      "rounded-full p-1.5 text-neutral-600 transition hover:bg-white hover:text-red-700",
                      FOCUS_RING,
                    )}
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}

      <RoutineDialog open={createOpen} onOpenChange={setCreateOpen} childId={childId} />

      <RoutineDialog
        open={Boolean(editing)}
        onOpenChange={(open) => !open && setEditing(undefined)}
        childId={childId}
        routine={editing}
      />

      <ConfirmDialog
        open={Boolean(deleting)}
        onOpenChange={(open) => !open && setDeleting(undefined)}
        title="Delete routine"
        description={`Are you sure you want to delete this routine${deleting ? ` "${deleting.label}"` : ""}?`}
        confirmLabel="Delete"
        destructive
        isPending={deleteMutation.isPending}
        onConfirm={() => {
          void handleDelete();
        }}
      />
    </SectionCard>
  );
}
